/* eslint-disable require-jsdoc */
import * as env from '../../utils/environment';
import isEmpty from '../../utils/is-empty';
import RtcError from '../../common/rtc-error';
import ErrorCode from '../../common/error-code';
import promiseRetry from '../../utils/retry';
import { isBoolean, isFunction, isNumber, isUndefined, stringify } from '../../utils/utils';
import { ErrorName } from '../../common/error-info';
import loggerManager from '../../utils/log/logger-manager';
import { generateErrorMessage } from '../../common/error-generator';
import { DeviceInfo, getCameras, getDevices, getMicrophones } from '../../common/device-detector';
import { IS_GET_CAPABILITIES_SUPPORTED } from '../rtc-detection';
import { CAPABILITIES_KEYS, KIBANA_EVENT } from '../constants';
import eventLog from '../../utils/log/event-log';
import { GetUserMediaOption } from '../..';
import { resumeContext } from '../../utils/audio-context';

const getUserMedia_ = async function (options: GetUserMediaOption) : Promise<MediaStream> {
  const constraints = buildConstraints(options);
  loggerManager.info(`getUserMedia with constraints: ${JSON.stringify(constraints)}`);

  let microphones: DeviceInfo[] = [];
  let cameras: DeviceInfo[] = [];
  const keysToInclude = ['label', 'deviceId', 'groupId'];
  if (constraints.audio) {
    microphones = await getMicrophones();
    loggerManager.info(`microphones: ${stringify(microphones.map(item => ({
      ...item,
      groupId: item.groupId.substring(0, 8)
    })), { keysToInclude })}`);
  }
  if (constraints.video) {
    cameras = await getCameras();
    loggerManager.info(`cameras: ${stringify(cameras, { keysToInclude })}`);
    // facingMode 如果是 user，且没有设置 cameraID 时，会采集到红外摄像头，因此需要指定另一个 front 摄像头
    if (!isBoolean(constraints.video) && constraints.video.facingMode === 'user' && !constraints.video.deviceId) {
      const filteredCameras = cameras.filter(camera => !camera.label.includes('infrared'));
      const frontCamera = filteredCameras.find(camera => camera.label.includes('facing front'));
      if (frontCamera) {
        constraints.video.deviceId = frontCamera.deviceId;
        loggerManager.info(`exclude infrared camera: ${JSON.stringify(constraints)}`);
      }
    }
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);

    if (IS_GET_CAPABILITIES_SUPPORTED) {
      stream.getTracks().forEach(track => {
        const capabilities = track.getCapabilities();
        loggerManager.info(`${track.kind} capabilities: ${stringify(capabilities, { keysToInclude: CAPABILITIES_KEYS })}`);
        if (!isUndefined(options.echoCancellation) && (capabilities.echoCancellation as (boolean | string)[])?.indexOf(options.echoCancellation) === -1) {
          loggerManager.warn(`Invalid argument for 'echoCancellation'. Expected one of [${JSON.stringify(capabilities.echoCancellation)}], but received '${options.echoCancellation}'`);
        }
      });
    }
    if (constraints.audio) resumeContext();
    return stream;
  } catch (error: any) {
    let { message } = error;
    if (error.name === 'NotFoundError') {
      // 由于没有摄像头or麦克风，但是尝试采集摄像头 or 麦克风导致采集失败。
      if (options.video && cameras && cameras.length === 0) {
        message = generateErrorMessage({ key: ErrorName.CAMERA_NOT_FOUND });
      }
      if (options.audio && microphones && microphones.length === 0) {
        message = generateErrorMessage({ key: ErrorName.MICROPHONE_NOT_FOUND });
      }
    }
    throw new RtcError({
      code: ErrorCode.INITIALIZE_FAILED,
      name: error.name,
      message,
      constraint: error.constraint
    });
  }
};

const getUserMedia = promiseRetry({
  retryFunction: getUserMedia_,
  settings: { retries: 3, timeout: 500 },
  onError: ({ error, retry, reject, retryFuncArgs, retriedCount }) => {
    const retryCount = retriedCount + 1;
    // 媒体设备无法访问时，增加重试。
    if (error.name === 'NotReadableError' || error.name === 'OverconstrainedError' || error.name === 'AbortError') {
      // 第一次重试
      if (retryCount === 1) {
        if (retryFuncArgs[0].video) {
          retryFuncArgs[0].maxResolution = false;
          // Safari 采集 1920 * 1280 时，分辨率描述 max 会采集失败，因此第一次重试去掉 max，不降帧率。
          if ((!env.IS_SAFARI || retryFuncArgs[0].width * retryFuncArgs[0].height <= 1920 * 1080) &&
            retryFuncArgs[0].frameRate
          ) {
            retryFuncArgs[0].frameRate = retryFuncArgs[0].frameRate > 10 ? 10 : 5;
          }
        }
        if (retryFuncArgs[0].retryWhenExactFailed && retryFuncArgs[0].useExactDeviceId) {
          retryFuncArgs[0].useExactDeviceId = false;
        }
      } else if (retryCount === 2) {
        // 第二次重试，只使用 deviceId 尝试恢复采集
        retryFuncArgs[0].useDeviceIdOnly = true;
      } else if (retryCount === 3 && !retryFuncArgs[0].useExactDeviceId) {
        // 最后一次重试，使用最原始 true/ false 的描述尝试恢复。eg: getUserMedia({ audio: true, video: true })
        retryFuncArgs[0].useTrueAsConstraint = true;
      }
      retry();
    } else {
      reject(error);
    }
    if (retryFuncArgs[0].microphoneId) {
      logCapabilities(retryFuncArgs[0].microphoneId, false);
    }
    if (retryFuncArgs[0].cameraId) {
      logCapabilities(retryFuncArgs[0].cameraId, true);
    }
  },
  onRetrying: retryCount => {
    loggerManager.warn(`getUserMedia NotReadableError observed, retrying [${retryCount}/3]`);
  },
  onRetryFailed: error => {
    eventLog.logFailedEvent({ eventType: KIBANA_EVENT.GET_USER_MEDIA_RETRY, error });
  },
  onRetrySuccess: retryCount => {
    eventLog.logSuccessEvent({ eventType: KIBANA_EVENT.GET_USER_MEDIA_RETRY });
    eventLog.uploadEvent({ log: `stat-${KIBANA_EVENT.GET_USER_MEDIA_RETRY}-success-${retryCount}` });
  }
});

async function logCapabilities(deviceId: string, isCamera: boolean) {
  const devices = isCamera ? await getCameras() : await getMicrophones();
  const targetDevice = devices.find(device => device.deviceId === deviceId);
  if (targetDevice) {
    // @ts-ignore
    isFunction(targetDevice.getCapabilities) && loggerManager.warn(stringify(targetDevice.getCapabilities(), { keysToInclude: CAPABILITIES_KEYS }));
  }
}

function buildConstraints(options: GetUserMediaOption) {
  return { audio: buildAudioConstraint(options), video: buildVideoConstraint(options) };
}

function buildAudioConstraint(options: GetUserMediaOption) {
  if (!options.audio) {
    return false;
  }
  if (options.useTrueAsConstraint) return true;
  const audioConstraints: MediaTrackConstraints | boolean = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: options.sampleRate
  };
  if (!isEmpty(options.microphoneId)) {
    audioConstraints.deviceId = options.useExactDeviceId ? { exact: options.microphoneId } : options.microphoneId;
    if (options.useDeviceIdOnly) return audioConstraints;
  }
  if (isNumber(options.channelCount)) {
    audioConstraints.channelCount = options.channelCount;
  }
  if (isBoolean(options.echoCancellation) || options.echoCancellation === 'remote-only' || options.echoCancellation === 'all') {
    audioConstraints.echoCancellation = options.echoCancellation;
  }
  if (isBoolean(options.noiseSuppression) && !options.noiseSuppression) {
    audioConstraints.noiseSuppression = false;
  }
  if (isBoolean(options.autoGainControl) && !options.autoGainControl) {
    audioConstraints.autoGainControl = false;
  }
  return isEmpty(audioConstraints) ? true : audioConstraints;
}

function buildVideoConstraint(options: GetUserMediaOption) {
  if (!options.video) return false;
  if (options.useTrueAsConstraint) return true;
  const { maxResolution = true } = options;
  const videoConstraints: MediaTrackConstraints | boolean = {};

  if (options.cameraId) {
    videoConstraints.deviceId = options.useExactDeviceId ? { exact: options.cameraId } : options.cameraId;
  } else if (options.facingMode) {
    videoConstraints.facingMode = options.facingMode;
  }

  if (options.useDeviceIdOnly && !isEmpty(videoConstraints)) return videoConstraints;

  if (options.width) {
    videoConstraints.width = { ideal: options.width };
    if (maxResolution && !env.IS_FIREFOX) {
      videoConstraints.width.max = options.width;
    }
  }
  if (options.height) {
    videoConstraints.height =  { ideal: options.height };
    if (maxResolution && !env.IS_FIREFOX) {
      videoConstraints.height.max = options.height;
    }
  }
  if (env.IS_FIREFOX && env.IS_MAC && options.width && options.height && options.width * options.height < 352 * 288) {
    // https://tapd.woa.com/TerWebG/prong/stories/view/1020396022878081013
    videoConstraints.width = options.width;
    videoConstraints.height = options.height;
  }

  if (options.frameRate) {
    videoConstraints.frameRate = options.frameRate;
  }
  return isEmpty(videoConstraints) ? true : videoConstraints;
}

export default getUserMedia;
